import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, useMediaQuery } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import CloseIcon from "@mui/icons-material/Close";
import { mirarPedidoAbierto, cerrarPedido } from "../helpers/mic-remoto";
import getClient from "../helpers/get-client";
import ModoMicrofono from "../views/ModoMicrofono";

/**
 * El aviso de "el computador pide dictar", disponible en TODA la app.
 *
 * Vive acá arriba y no dentro del Modo Turno a propósito: el médico puede estar
 * en el visualizador, en recetas o en su perfil cuando decide dictar desde el
 * computador. Obligarlo a navegar al Turno en el celular para poder aceptar
 * sería exactamente la fricción que esto viene a sacar. Basta con tener la app
 * abierta.
 *
 * Al aceptar se abre el Modo Micrófono a pantalla completa, que tapa lo que
 * hubiera detrás: acá el celular es un micrófono, no una ficha.
 */
const INTERVALO_MS = 3000;

const MicRemotoGate = () => {
  const esMobile = useMediaQuery("(max-width: 768px)");
  const [disponible, setDisponible] = useState(false);
  const [pedido, setPedido] = useState(null);
  const [enMicrofono, setEnMicrofono] = useState(null);
  const descartadosRef = useRef(new Set());

  // ¿Este backend sabe hacer dictado remoto? Sin la bandera no se sondea nada:
  // así el front se puede deployar antes que el backend sin pegarle a un 404
  // cada 3 segundos.
  useEffect(() => {
    if (!esMobile) return undefined;
    let vivo = true;
    getClient()
      .get("/api/medical_analysis/asr/config/")
      .then((r) => { if (vivo) setDisponible(Boolean(r.data?.mic_remoto)); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [esMobile]);

  const preguntar = useCallback(async () => {
    if (document.visibilityState !== "visible") return;
    try {
      const abierto = await mirarPedidoAbierto();
      if (!abierto || abierto.status !== "pending") { setPedido(null); return; }
      if (descartadosRef.current.has(abierto.id)) return;
      setPedido(abierto);
    } catch (err) {
      // Un 403 no es un fallo pasajero: es el servidor diciendo que ESTA cuenta
      // no tiene el Modo Turno habilitado, y eso no cambia por reintentar. Hasta
      // el 21-sep-2026 este catch se lo tragaba igual que a un timeout, así que
      // el celular de un médico sin el módulo golpeaba la puerta cada 3 segundos
      // para siempre: Hugo Inzulza acumuló 130 respuestas 403 en 15 minutos el
      // día que entró y no podía hacer nada, y 367 de los 383 errores 403 de esa
      // semana entera salieron de este solo endpoint (Planner #1289,
      // INC-2026-31). La bandera de arriba responde "este backend sabe hacer
      // dictado remoto"; esta responde "esta cuenta puede", que es otra pregunta
      // y solo la contesta el servidor.
      if (err?.response?.status === 403) {
        setDisponible(false);
        return;
      }
      /* el resto, silencioso: un sondeo fallido no puede molestar al médico */
    }
  }, []);

  useEffect(() => {
    // Sin la bandera, en escritorio, o ya dentro del Modo Micrófono: no se
    // sondea. Con la pestaña oculta tampoco (lo chequea `preguntar`): son
    // consultas cada 3 s y no tiene sentido gastarlas en una pantalla que nadie
    // está mirando.
    if (!esMobile || !disponible || enMicrofono) return undefined;
    preguntar();
    const timer = setInterval(preguntar, INTERVALO_MS);
    const alVolver = () => preguntar();
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("focus", alVolver);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("focus", alVolver);
    };
  }, [esMobile, disponible, enMicrofono, preguntar]);

  if (enMicrofono) {
    return (
      <ModoMicrofono
        pedido={enMicrofono}
        onSalir={() => { setEnMicrofono(null); setPedido(null); }}
      />
    );
  }

  if (!pedido) return null;

  return (
    <Box
      data-testid="pedido-mic-global"
      sx={{
        position: "fixed",
        top: "calc(8px + env(safe-area-inset-top))",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1900,
        width: "min(460px, 94vw)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: "14px",
        bgcolor: "var(--bg-elevated)",
        border: "1px solid rgba(var(--color-primary-rgb),0.55)",
        boxShadow: "0 18px 50px rgba(var(--shadow-rgb),0.6)",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: 11, letterSpacing: 0.6, color: "var(--text-faint)", textTransform: "uppercase" }}>
          El computador pide dictar
        </Box>
        {/* El nombre del paciente es lo que evita dictar en la ficha equivocada
            cuando hay dos médicos en la misma sala. */}
        <Box sx={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {pedido.paciente || `Cama ${pedido.row}`}
        </Box>
        <Box sx={{ fontSize: 12, color: "var(--text-secondary)" }}>{pedido.column}</Box>
      </Box>
      <Button
        size="small"
        variant="contained"
        data-testid="tomar-dictado-global"
        onClick={() => setEnMicrofono(pedido)}
        startIcon={<MicIcon sx={{ fontSize: 16 }} />}
        sx={{ textTransform: "none", borderRadius: "999px", minHeight: 40, px: 2, flexShrink: 0 }}
      >
        Dictar
      </Button>
      <IconButton
        size="small"
        aria-label="Descartar pedido de dictado"
        onClick={() => {
          // Descartado en este aparato: no se vuelve a ofrecer el mismo pedido,
          // pero NO se cancela en el servidor — otro aparato del médico todavía
          // puede tomarlo.
          descartadosRef.current.add(pedido.id);
          setPedido(null);
          cerrarPedido(pedido.id, false);
        }}
        sx={{ color: "var(--text-faint)", flexShrink: 0 }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

export default MicRemotoGate;
